import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products/featured/smart`),
          axios.get(`${API}/categories`),
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Header />

      {/* Hero Section - Luxury Dark Layout */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-foreground tracking-tight leading-[1.1]">
                Curated Luxury.
                <br />
                <span className="text-primary">Exclusively Yours.</span>
              </h1>
              <p className="font-body text-base md:text-lg text-foreground/60 mt-8 leading-relaxed max-w-md">
                Discover a world of rare finds and timeless elegance from 
                independent artisans and renowned craftsmen.
              </p>
              <div className="mt-10">
                <Link to="/featured">
                  <Button 
                    size="lg" 
                    className="rounded-sm px-10 py-7 font-medium bg-primary hover:bg-primary/90 text-primary-foreground tracking-wide"
                    data-testid="explore-collection-btn"
                  >
                    Explore the Collection
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5] lg:aspect-[3/4]">
                <img
                  src="https://customer-assets-agu9un31.emergentagent.net/job_festive-preorder-hub/artifacts/vdv6vr8f_home%20page%20image.jpg"
                  alt="Artisan Bridge Home - Curated luxury decor"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <a 
          href="#categories" 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-foreground/40 hover:text-primary transition-colors"
        >
          <ChevronDown className="w-8 h-8 animate-bounce" />
        </a>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-24 md:py-32 border-t border-border/50" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Collections
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-3">
                Browse by Category
              </h2>
            </div>
            <Link to="/categories" className="text-foreground/60 hover:text-primary font-medium flex items-center gap-2 transition-colors text-sm tracking-wide">
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="group relative rounded-lg overflow-hidden h-80 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`category-card-${category.id}`}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-xl text-white group-hover:text-primary transition-colors duration-300">
                    {category.name}
                  </h3>
                  <span className="inline-flex items-center gap-2 text-xs text-white/60 mt-2 uppercase tracking-wider">
                    Explore
                    <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 md:py-32 border-t border-border/50" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Curated Selection
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-3">
                Featured Pieces
              </h2>
            </div>
            <Link to="/featured" className="text-foreground/60 hover:text-primary font-medium flex items-center gap-2 transition-colors text-sm tracking-wide">
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-card rounded-lg overflow-hidden border border-border/50">
                  <div className="aspect-square bg-muted animate-pulse" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-muted animate-pulse rounded w-3/4" />
                    <div className="h-4 bg-muted animate-pulse rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product, index) => (
                <div 
                  key={product.id} 
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
              Get in Touch
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
              Interested in a Piece?
            </h2>
            <p className="text-foreground/60 mt-6 leading-relaxed">
              Send us an enquiry for any pieces that catch your eye. 
              Our team will get back to you with availability and details.
            </p>
            <Link to="/featured">
              <Button 
                size="lg" 
                className="mt-10 rounded-sm px-10 py-7 font-medium bg-primary hover:bg-primary/90 text-primary-foreground tracking-wide"
                data-testid="preorder-cta-btn"
              >
                Browse Collection
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
